import React, { Fragment } from 'react';
import classnames from 'classnames';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Thumbnail from '../../../Media/Thumbnail/Thumbnail';
import ButtonHoverRender from '../../../Buttons/ButtonHover/ButtonHoverRender';
import ButtonHoverPrimary from '../../../Buttons/ButtonHover/ButtonHoverPrimary';
import Icon from '../../../Icon/Icons';
import { withTranslation } from 'react-i18next';
import * as constants from '../../../../constants';
import './TourControls.scss';

class TourControls extends React.Component {
  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.activeStop !== nextProps.activeStop ||
      this.props.restPosition !== nextProps.restPosition ||
      this.props.tourStops !== nextProps.tourStops ||
      this.props.t !== nextProps.t
    );
  }

  componentDidUpdate(prevProps) {
    const { restPosition } = this.props;
    // check that restPosition is a number because its default state is 'false'
    // otherwise focus goes to gallery button on each render
    if (
      typeof prevProps.restPosition === 'number' &&
      prevProps.restPosition !== restPosition
    ) {
      if (restPosition > 0) {
        this.galleryButton?.focus();
      }
    }
  }

  renderThumbs = (thumbnails = []) => {
    const { activeStop, handleChange } = this.props;
    return thumbnails.map((thumb, i) => {
      if (!thumb) return null;
      const thumbClass = this.handleThumbClasses(i, thumbnails.length);
      const {
        panoId,
        id,
        mainMediaType,
        thumbnail,
        order,
        name,
        defaultPos,
      } = thumb;

      const showName = activeStop !== i;
      return (
        <Thumbnail
          mediaId={panoId || id}
          mediaType={mainMediaType || constants.PANOS} // default to panos for old stop images that dont have the mainMediaType key/value
          thumbSrc={thumbnail}
          key={panoId + i || id + i}
          thumbClass={`media-thumbnail ${thumbClass}`}
          height={90}
          name={showName && name}
          alt=""
          view
          overlayMediaName
          data-cy={`stop-selector-${id}`}
          handleClick={() =>
            handleChange({
              defaultPos,
              stopNum: order || i,
              stopName: name,
              origin: 'Gallery',
            })
          }
        />
      );
    });
  };

  handleThumbClasses = (thumbIndex, thumbsLength) => {
    const { activeStop } = this.props;
    const thumbClass = {
      active: thumbIndex === activeStop, // active thumbnail
      ...(thumbsLength >= 2 && {
        activeNext__right:
          thumbIndex === activeStop + 1 || // thumbnail to the immediate right of selected
          thumbIndex === activeStop - (thumbsLength - 1), // thumbnail one when last thumbnail is selected
      }),
      ...(thumbsLength >= 3 && {
        activeNext__left:
          thumbIndex === activeStop - 1 || // thumbnail to the immediate left of selected
          thumbIndex === activeStop + thumbsLength - 1, // last thumbnail when first thumbnail is selected
      }),
      ...(thumbsLength >= 4 && {
        activePlusOne__right:
          thumbIndex === activeStop + 2 || // outer thumbnail on right of selected
          thumbIndex === activeStop - (thumbsLength - 2), // outer thumbnail on right when second last or last thumbnails are selected
      }),
      ...(thumbsLength >= 5 && {
        activePlusOne__left:
          thumbIndex === activeStop - 2 || // outer thumbnail on left of selected
          thumbIndex === activeStop + thumbsLength - 2, // outer thumbnail on left when first or second thumbnails are selected
      }),
      ...(thumbsLength >= 6 && {
        outerHidden__right:
          thumbIndex === activeStop + 3 || // outer hidden thumbnail on right of selected
          thumbIndex === activeStop - (thumbsLength - 3), // outer hidden thumbnail on right when third last, second last or last thumbnails are selected
      }),
      ...(thumbsLength >= 7 && {
        outerHidden__left:
          thumbIndex === activeStop - 3 || // // outer hidden thumbnail on left of selected
          thumbIndex === activeStop + thumbsLength - 3, // outer hidden thumbnail on right when first, second or third thumbnails are selected
      }),
    };

    return classnames(thumbClass);
  };

  render() {
    const {
      thumbnailList,
      activeStop,
      handleChange,
      stopGallery,
      primaryColor,
      tourStops,
      miniMapView,
      setYPosition,
      tier,
      t,
    } = this.props;

    const sectionClasses = classnames({
      'stop-gallery-thumbs': stopGallery,
    });
    const shouldShowControls = tourStops && tourStops.length > 1;
    const controlWrapperClasses = cx({
      'btn-wrapper': true,
      single: !shouldShowControls,
      'map-view': miniMapView === 'pano',
      starter: tier === 'starter',
    });

    const isMobile = window.innerWidth < 1025;
    const stopName = tourStops[activeStop]?.name;
    return (
      <Fragment>
        <nav id="tour-controls-container" aria-label="360 tour" className={sectionClasses}>
          <div className="stop-name-wrapper">
            <div
              id='tour-controls-stop-name'
              data-cy="tour-controls__stop-name"
              className="stop-name"
              aria-live="polite"
              aria-label={`Now visiting: ${stopName}.`}
            >
              {stopName}
            </div>
          </div>
          <div className='thumbnail-wrapper'>
            {!isMobile && this.renderThumbs(thumbnailList)}
          </div>
          {shouldShowControls && (
            <div
              className={controlWrapperClasses}
            >
              <ButtonHoverRender
                render={({onEnter, onLeave, hovering}) => (
                  <ButtonHoverPrimary
                    onEnter={onEnter}
                    onLeave={onLeave}
                    hovering={hovering}
                    className="prev-btn"
                    color={primaryColor}
                    backgroundColor="#fff"
                    handleClick={() =>
                      handleChange({
                        defaultPos: tourStops[activeStop].defaultPos,
                        stopNum: activeStop - 1,
                        stopName: null,
                        origin: 'Prev',
                      })
                    }
                    aria-controls="tour-controls-stop-name"
                    isMobile={isMobile}
                    data-cy="tour-controls__prev-btn"
                  >
                    <Icon>play_circle_filled</Icon>
                    &nbsp; {t('publicTour.tourControls.backButton')}
                  </ButtonHoverPrimary>
                )}
              />
              <ButtonHoverRender
                render={({ onEnter, onLeave, hovering }) => (
                  <ButtonHoverPrimary
                    myRef={btn => (this.galleryButton = btn)}
                    onEnter={onEnter}
                    onLeave={onLeave}
                    hovering={hovering}
                    className="counter"
                    color={primaryColor}
                    backgroundColor="#fff"
                    handleClick={() => setYPosition(0)}
                    aria-expanded="false"
                    isMobile={isMobile}
                    data-cy="show-all-stops"
                  >
                    <Icon>apps</Icon>
                    {t('publicTour.ada.tourControls.totalStops',
                      {
                        currentStop: `${activeStop + 1}`,
                        totalStops: `${tourStops && tourStops.length}`
                      }
                    )}
                    <span className="sr-only">
                      {t('publicTour.ada.tourControls.showAllCopy')}
                    </span>
                  </ButtonHoverPrimary>
                )}
              />
              <ButtonHoverRender
                render={({ onEnter, onLeave, hovering }) => (
                  <ButtonHoverPrimary
                    onEnter={onEnter}
                    onLeave={onLeave}
                    hovering={hovering}
                    className="next-btn"
                    color={primaryColor}
                    backgroundColor="#fff"
                    handleClick={() =>
                      handleChange({
                        defaultPos:
                          tourStops && tourStops[activeStop].defaultPos,
                        stopNum: activeStop + 1,
                        stopName: null,
                        origin: 'Next',
                      })
                    }
                    aria-controls="tour-controls-stop-name"
                    isMobile={isMobile}
                    data-cy="tour-controls__next-btn"
                  >
                    {t('publicTour.tourControls.nextButton')} &nbsp;
                    <Icon>play_circle_filled</Icon>
                  </ButtonHoverPrimary>
                )}
              />
            </div>
          )}
        </nav>
      </Fragment>
    );
  }
}

TourControls.propTypes = {
  tourStops: PropTypes.array.isRequired,
  activeStop: PropTypes.number.isRequired,
};

export default withTranslation()(TourControls);
